import { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import CheckCircle from "@mui/icons-material/CheckCircle";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  fetchSetupAccountRefreshTokens,
  saveConnectedAccount,
} from "../../helper/channelIntegrattion/amazonAccountStore";
import {
  buildAmazonConfirmUrl,
  buildSellerConsentUrl,
  buildVendorConsentUrl,
  isAllowedAmazonCallback,
  onboardingRedirectUri,
  parseOnboardingState,
  portalFromAmazonCallback,
  readOnboardingPortal,
  readStoredClientId,
  rememberOnboardingPortal,
  resolveAmazonCallbackUri,
} from "./amazonSpApi";

const tokenExchangeByCode = new Map();
const onboardingByCode = new Map();

const exchangeAmazonToken = (code) => {
  if (!tokenExchangeByCode.has(code)) {
    const request = axios
      .post(`${process.env.REACT_APP_ADS_API}/clients:exchange-amazon-spapi-oauth-code`, {
        code,
        redirect_uri: onboardingRedirectUri(),
      })
      .then((response) => response.data || {})
      .catch((error) => ({
        error: error?.response?.data || error?.message || String(error),
      }));
    tokenExchangeByCode.set(code, request);
  }
  return tokenExchangeByCode.get(code);
};

const completeOnboarding = ({
  code,
  sellingPartnerId,
  clientId,
  accountType,
  accountName,
  tokenId,
}) => {
  if (!onboardingByCode.has(code)) {
    const request = (async () => {
      const tokenResult = await exchangeAmazonToken(code);
      if (!tokenResult || tokenResult.error || !tokenResult.refresh_token) {
        return {
          tokenResult,
          saved: null,
          accountName: "",
          saveError: "Amazon did not complete the connection. Go back and try again.",
        };
      }

      const setupTokens = await fetchSetupAccountRefreshTokens(clientId);
      try {
        const saved = await saveConnectedAccount({
          clientId,
          tokenType: accountType,
          refreshToken: tokenResult.refresh_token,
          sellingPartnerId,
          accountName: accountName || sellingPartnerId,
          tokenId,
          setupTokens,
        });
        return {
          tokenResult,
          saved,
          accountName: saved.account_name,
          reconnected: saved.reconnected,
          saveError: "",
        };
      } catch (error) {
        const saveError = error?.message || "Unable to save the Amazon account";
        return {
          tokenResult,
          saved: null,
          accountName: "",
          reconnected: false,
          saveError,
        };
      }
    })();
    onboardingByCode.set(code, request);
  }
  return onboardingByCode.get(code);
};

const SummaryRow = ({ label, value }) => (
  <Box display="flex" justifyContent="space-between" gap={2}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-all", textAlign: "right" }}>
      {value || "—"}
    </Typography>
  </Box>
);

const accountTypeLabel = (accountType) => {
  if (accountType === "vendor") return "Vendor";
  if (accountType === "seller") return "Seller";
  return "";
};

const AmazonOnboardingCallback = () => {
  const dashboardSideBarVisibiltyState = useSelector(
    (state) => state.dashboardData.dashboardSideBarVisibiltyState
  );
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const startPortal = searchParams.get("start");
  const startClientId = searchParams.get("clientId");
  const startAccountName = searchParams.get("accountName") || "";
  const startTokenId = searchParams.get("tokenId") || "";
  const isStart = startPortal === "seller" || startPortal === "vendor";
  const callbackUri = resolveAmazonCallbackUri(
    searchParams.get("amazon_callback_uri")
  );
  const amazonState = searchParams.get("amazon_state");
  const version = searchParams.get("version");
  const code = searchParams.get("spapi_oauth_code");
  const sellingPartnerId = searchParams.get("selling_partner_id");
  const state = searchParams.get("state");
  const parsedState = parseOnboardingState(state);
  const [savedAccount, setSavedAccount] = useState(null);
  const [reconnected, setReconnected] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [tokenLoading, setTokenLoading] = useState(Boolean(code) && !callbackUri && !isStart);
  const [redirecting, setRedirecting] = useState(Boolean(callbackUri) || isStart);
  const [handoffError, setHandoffError] = useState("");
  const accountType = readOnboardingPortal();

  useEffect(() => {
    if (!isStart) return;
    rememberOnboardingPortal(startPortal);
    const clientId = startClientId || readStoredClientId();
    const url =
      startPortal === "vendor"
        ? buildVendorConsentUrl(clientId, startAccountName, startTokenId)
        : buildSellerConsentUrl(clientId, startAccountName, startTokenId);
    window.location.replace(url);
  }, [isStart, startPortal, startClientId, startAccountName, startTokenId]);

  useEffect(() => {
    if (!callbackUri || isStart) return;

    if (!isAllowedAmazonCallback(callbackUri)) {
      setRedirecting(false);
      setHandoffError("Amazon returned a confirm link this page will not open.");
      return;
    }

    const portal = portalFromAmazonCallback(callbackUri);
    if (portal) rememberOnboardingPortal(portal);

    const confirmUrl = buildAmazonConfirmUrl({
      callbackUri,
      amazonState,
      version,
      clientId: readStoredClientId(),
    });
    window.location.replace(confirmUrl);
  }, [callbackUri, amazonState, version, isStart]);

  useEffect(() => {
    if (callbackUri || isStart) return;

    const portal = readOnboardingPortal();
    const clientId = parsedState.clientId || readStoredClientId();

    if (!code) {
      setTokenLoading(false);
      return;
    }

    let active = true;
    setTokenLoading(true);

    const finish = (portal === "seller" || portal === "vendor")
      ? completeOnboarding({
          code,
          sellingPartnerId,
          clientId,
          accountType: portal,
          accountName: parsedState.accountName,
          tokenId: parsedState.tokenId,
        })
      : exchangeAmazonToken(code).then((result) => ({
          tokenResult: result,
          saved: null,
          accountName: "",
          reconnected: false,
          saveError: "",
        }));

    finish.then((result) => {
      if (!active) return;
      setSavedAccount(result.saved);
      setReconnected(Boolean(result.reconnected));
      setSaveError(result.saveError || "");
      setTokenLoading(false);
    });

    return () => {
      active = false;
    };
  }, [callbackUri, code, isStart, parsedState.accountName, parsedState.clientId, parsedState.tokenId, sellingPartnerId, state]);

  const integrationPath =
    accountType === "vendor" || savedAccount?.token_type === "vendor"
      ? "/integrations?selectedChannel=amazon&subConnection=vendor"
      : "/integrations?selectedChannel=amazon&subConnection=seller";

  const displayName = savedAccount?.account_name || parsedState.accountName;
  const displayType = accountTypeLabel(savedAccount?.token_type || accountType || startPortal);
  const connected = Boolean(savedAccount) && !saveError;
  const failed = Boolean(saveError || handoffError) && !tokenLoading && !redirecting;

  return (
    <div
      style={{ marginTop: "72px" }}
      className={
        dashboardSideBarVisibiltyState
          ? "main-content-container-onopen-mode"
          : "main-content-container-oncolapse-mode"
      }
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="calc(100vh - 72px)"
        px={3}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 440,
            border: "1px solid #E0E0E0",
            borderRadius: "12px",
            bgcolor: "#fff",
            px: 4,
            py: 5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 2,
          }}
        >
          {redirecting || tokenLoading ? (
            <>
              <CircularProgress size={42} />
              <Typography variant="h6" fontWeight={600}>
                {isStart
                  ? `Opening ${displayType || "Amazon"} Central`
                  : "Connecting your Amazon account"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isStart
                  ? "You will continue on Amazon to authorize this account."
                  : "Please wait while we finish the connection."}
              </Typography>
            </>
          ) : null}

          {failed ? (
            <>
              <ErrorOutline sx={{ fontSize: 56, color: "#D32F2F" }} />
              <Typography variant="h6" fontWeight={600}>
                Amazon account was not connected
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {saveError || handoffError}
              </Typography>
              <Button variant="contained" onClick={() => navigate(integrationPath)}>
                Go to Integration page
              </Button>
            </>
          ) : null}

          {!redirecting && !tokenLoading && !failed ? (
            <>
              <CheckCircle sx={{ fontSize: 64, color: "#1B7F3A" }} />
              <Typography variant="h6" fontWeight={600}>
                {connected
                  ? reconnected
                    ? "Your Amazon account is reconnected"
                    : "Your Amazon account is connected"
                  : "Amazon authorization finished"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {connected
                  ? "You can manage this account from the Integration page."
                  : "Return to the Integration page to review your accounts."}
              </Typography>
              {connected ? (
                <Box
                  width="100%"
                  sx={{
                    bgcolor: "#F7F6FF",
                    borderRadius: "8px",
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    textAlign: "left",
                  }}
                >
                  <SummaryRow label="Account name" value={displayName} />
                  <SummaryRow label="Account type" value={displayType} />
                  <SummaryRow label="Selling partner id" value={sellingPartnerId} />
                </Box>
              ) : null}
              <Button variant="contained" onClick={() => navigate(integrationPath)}>
                Go to Integration page
              </Button>
            </>
          ) : null}
        </Box>
      </Box>
    </div>
  );
};

export default AmazonOnboardingCallback;
